import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cx } from './cx';
import { Pulse } from './Chip';

/**
 * Background work, one pattern everywhere: what · how far · how long · what happens next ·
 * safe to leave. A navy "working" panel; the dock shows the same thing on every page.
 */
export interface DeskJob {
  id: string;
  title: string;             // "Writing your November plan"
  eta?: string | null;       // "~1 min"
  progress?: number | null;  // 0..1; null = we can't say how far
  detail?: string | null;    // "Using October's results · checking it against your strategy"
  to?: string;               // where to watch it
}

export const ProgressTrack: React.FC<{ value?: number | null; className?: string; label?: string }> = ({ value, className, label }) => (
  <div className={cx('h-1.5 overflow-hidden rounded-full bg-navy-3', className)} role="progressbar" aria-label={label}
       aria-valuemin={0} aria-valuemax={100} aria-valuenow={value == null ? undefined : Math.round(value * 100)}>
    <div className={cx('h-full rounded-full bg-work-bar', value == null && 'w-1/3 animate-pulse')}
         style={value == null ? undefined : { width: `${Math.max(4, Math.min(100, value * 100))}%` }} />
  </div>
);

export const WorkingPanel: React.FC<{ job: DeskJob; footnote?: React.ReactNode; className?: string; compact?: boolean }> = ({
  job, footnote = 'Safe to leave. We’ll notify you here when it’s done.', className, compact,
}) => (
  <div className={cx('flex flex-col gap-3 rounded-card bg-navy text-on-navy', compact ? 'p-4' : 'p-5', className)} aria-live="polite">
    <div className="flex items-start justify-between gap-3">
      <span className="flex items-center gap-2.5 font-semibold"><Pulse />{job.title}</span>
      {job.eta && <span className="shrink-0 font-money text-small text-on-navy-3">{job.eta}</span>}
    </div>
    <ProgressTrack value={job.progress} label={job.title} />
    {job.detail && <div className="text-small text-on-navy-2">{job.detail}</div>}
    {footnote && <div className="text-xs text-on-navy-3">{footnote}</div>}
    {job.to && !compact && <Link to={job.to} className="self-start text-small font-semibold text-brand hover:underline">Watch it</Link>}
  </div>
);

const DOCK_KEY = 'gd.jobDock.collapsed';

/** Docked bottom-right while anything runs; collapses to the "1 job running" pill. */
export const useDockCollapsed = () => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(DOCK_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(DOCK_KEY, collapsed ? '1' : '0'); } catch { /* storage unavailable */ }
  }, [collapsed]);
  return [collapsed, setCollapsed] as const;
};

export const JobDock: React.FC<{ jobs: DeskJob[]; collapsed?: boolean; onCollapsedChange?: (c: boolean) => void; className?: string }> = ({
  jobs, collapsed: controlled, onCollapsedChange, className,
}) => {
  const [own, setOwn] = useDockCollapsed();
  const collapsed = controlled ?? own;
  const setCollapsed = (f: (c: boolean) => boolean) => (onCollapsedChange ? onCollapsedChange(f(collapsed)) : setOwn(f));
  if (!jobs.length) return null;
  const label = `${jobs.length} job${jobs.length === 1 ? '' : 's'} running`;
  return (
    <div className={cx('fixed bottom-4 right-4 z-[1030] flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2 sm:bottom-6 sm:right-6', className)}>
      {!collapsed && (
        <div className="flex w-[360px] max-w-full flex-col gap-2 rounded-panel bg-navy p-2 shadow-decide">
          {jobs.map(j => j.to ? (
            <Link key={j.id} to={j.to} className="block rounded-card no-underline hover:bg-navy-2">
              <WorkingPanel job={j} compact footnote={null} className="bg-transparent" />
            </Link>
          ) : <WorkingPanel key={j.id} job={j} compact footnote={null} className="bg-transparent" />)}
          <div className="px-3 pb-2 text-xs text-on-navy-3">Safe to leave — we’ll notify you here.</div>
        </div>
      )}
      <button type="button" onClick={() => setCollapsed(c => !c)} aria-expanded={!collapsed}
        className="inline-flex min-h-[40px] items-center gap-2 rounded-full bg-navy px-4 text-small font-semibold text-on-navy shadow-decide">
        <Pulse />{label}
        <ChevronDown className={cx('h-4 w-4 transition-transform', !collapsed && 'rotate-180')} aria-hidden="true" />
      </button>
    </div>
  );
};

/** The "1 job running" pill for top bars (opens nothing by itself; pair with the dock). */
export const JobPill: React.FC<{ count: number; onClick?: () => void }> = ({ count, onClick }) => count ? (
  <button type="button" onClick={onClick}
    className="inline-flex min-h-[36px] items-center gap-2 rounded-full bg-work-soft px-3 text-small font-semibold text-work">
    <Pulse />{count} job{count === 1 ? '' : 's'} running
  </button>
) : null;
